import axiosConfig from "./axiosConfig";

export interface RygMetric {
  pct: number | null;
  ryg: "red" | "amber" | "green";
}

export interface ProbabilityRygMetric {
  pctP50: number | null;
  pctP90: number | null;
  ryg: "red" | "amber" | "green";
}

export interface RevenueMetrics {
  grossRevenue: number;
  arbitrage: number;
  licenseNetArr: number;
  leaseNetArr: number;
}

export interface ProbabilityRevenue {
  p50: RevenueMetrics;
  p90: RevenueMetrics;
}

export interface SiteProbabilityMetrics {
  // Annual Y1 forecast (only populated for p50/p90)
  generationMwh?: number;
  demandMwh?: number;
  deliveredOnSitePct?: number;
  demandFulfilledByIrPct?: number;
  // Cumulative forecast comparison
  cumulativeGenerationMwh?: number;
  cumulativeDemandMwh?: number;
  cumulativeOnSiteMwh?: number;
  cumulativeExportMwh?: number;
  // Pct vs actual (only populated for p50/p90)
  cumulativeGenerationPct?: number | null;
  cumulativeDemandPct?: number | null;
  cumulativeOnSitePct?: number | null;
  cumulativeExportPct?: number | null;
  // Delivered on-site % (percentage-point diff: actual% - forecast%)
  cumulativeDeliveredOnSitePctPoints?: number | null;
}

export interface OperationalDeploymentStatsResponse {
  currencyCode: string;
  capacity: {
    siteCount: number;
    inProgressSiteCount: number;
    pvMw: number;
    batteryMwh: number;
  };
  energy: {
    totalGenerationMwhP50: number;
    totalGenerationMwhP90: number;
    totalDemandMwh: number;
    deliveredOnSitePctP50: number;
    deliveredOnSitePctP90: number;
    demandFulfilledByIrPctP50: number;
    demandFulfilledByIrPctP90: number;
  };
  financial: {
    revenue: Record<string, ProbabilityRevenue>;
    licenseRevenue: Record<string, ProbabilityRevenue>;
    leaseRevenue: Record<string, ProbabilityRevenue>;
    licenseSites: number;
    leaseSites: number;
    landlordCount: number;
    tenantCount: number;
  };
  forecastComparison: {
    generation: ProbabilityRygMetric;
    curtailmentLossMwh?: number;
    demand: ProbabilityRygMetric;
    demandExTakeOrPay: ProbabilityRygMetric;
    onSite: ProbabilityRygMetric;
    onSiteExTakeOrPay: ProbabilityRygMetric;
    deliveredOnSitePct: ProbabilityRygMetric;
    export: ProbabilityRygMetric;
    customerRevenue: ProbabilityRygMetric;
    meteredVsSupplierExport: RygMetric;
  };
  performance: {
    red: number;
    amber: number;
    green: number;
    blue?: number;
  };
  health: {
    currentIssues: number | null;
    currentlyHealthy: number | null;
    lastMonthIssues: number | null;
    lastMonthHealthy: number | null;
  };
  sites: SiteMetrics[];
}

export interface InProgressDeploymentStatsResponse {
  sites: SiteMetrics[];
}

export type DeploymentStatsScope = "operational" | "inProgress";

export interface SiteMetrics {
  // Identity
  id: string;
  name: string;
  cod?: string | null;
  operationalStatus?: string | null;
  landlordName?: string | null;
  tenantName?: string | null;
  isOwnerOccupier?: boolean;
  // Capacity
  pvKw?: number;
  batteryMwh?: number;
  // Energy (keyed by "p50", "p90", "actual")
  energy?: Record<string, SiteProbabilityMetrics>;
  // Financial (keyed by currency, then p50/p90)
  currencyCode?: string;
  financials?: Record<string, ProbabilityRevenue>;
  investmentModel?: string | null;
  // Trailing-12-month customer revenue, keyed by currency code (native + GBP).
  // Selected per active investment model + ownership, mirroring perf/forecast page.
  // Actual + matching P50 / P90 forecast values are exposed in parallel
  // dictionaries so the frontend can render an actual-vs-forecast comparison.
  cumulativeCustomerRevenue?: Record<string, number>;
  cumulativeCustomerRevenueP50?: Record<string, number>;
  cumulativeCustomerRevenueP90?: Record<string, number>;
  cumulativeCustomerRevenuePctP50?: number | null;
  cumulativeCustomerRevenuePctP90?: number | null;
  forecastCustomerRevenueRyg?: string;
  // Take-or-pay
  takeOrPayMinKwh?: number | null;
  // Curtailment
  curtailmentPending?: boolean;
  curtailmentLossMwh?: number;
  // Forecast comparison RYG
  forecastGenerationRyg?: string;
  forecastDemandRyg?: string;
  forecastOnSiteRyg?: string;
  forecastDeliveredOnSitePctRyg?: string;
  forecastExportRyg?: string;
  monthsAnalysed?: number;
  latestMonthAnalysed?: string | null;
  performanceRyg?: string;
  // Split health dimensions: live operational state vs last billed month analysis.
  currentHealthStatus?: string | null;
  lastMonthHealthStatus?: string | null;
  // Transfer / metered
  meteredVsSupplierExportInrangeMwh?: number;
  meteredVsSupplierExportGridMwh?: number;
  meteredVsSupplierExportPct?: number | null;
  meteredVsSupplierExportRyg?: string;
  hasGridMeteredData?: boolean;
  monthsWithoutGridActuals?: number | null;
  // Health
  healthStatus?: string | null;
  healthDetail?: SiteHealthDetail;
}

export interface HealthEventSummary {
  checkType: string;
  severity: string;
  summary: string;
  source: string;
}

export interface SiteHealthDetail {
  liveEvents: HealthEventSummary[];
  monthlyEvents: HealthEventSummary[];
  analysisMonth: string | null;
}

export interface DeploymentStatsInitResponse {
  operationKey: string;
  status: "pending" | "running";
}

const postOperationalDeploymentStats = async (
  authToken: string,
  scope: DeploymentStatsScope
): Promise<DeploymentStatsInitResponse> => {
  const { data } = await axiosConfig.post(
    `/operational/deployment-stats`,
    {},
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      params: { scope },
    }
  );
  return data;
};

export default postOperationalDeploymentStats;
